import React, { useRef, useEffect, useState } from 'react';
import InteractiveCropOverlay from '../../components/studio/InteractiveCropOverlay';
import { applyBrightness, applyContrast, applyGrayscale, applySepia, applyBlur } from '../../utils/canvasFilters';
import { executeAICommand } from '../../utils/aiCommandParser';
import { motion, AnimatePresence } from 'framer-motion';

const StudioCanvas = ({
  previewDisplayUrl,
  selectedFileName,
  renderedPreviewUrl,
  canvasFrameStyle,
  previewImageStyle,
  resizeOverlayStyle,
  isResizing,
  resizeAspectLabel,
  targetWidthLabel,
  targetHeightLabel,
  originalAspectLabel,
  onResizeStart,
  watermarkPreview,
  onOpenFilePicker,
  isBatchMode,
  isCropMode,
  cropState,
  onCropChange,
  onCropComplete,
  cropAspectRatio,
  onCropAspectRatioChange,
  onExitCropMode,
  imageSize,
  cropOperation,
  effectOperation,
  zoom,
  onZoomChange,
  pan,
  onPanChange,
  activeOperation,
  resizeOperation,
  onResizeChange,
  onResizeComplete,
  resizeAspectRatio,
  onResizeAspectRatioChange,
  onFocusNode,
  onUpdateOperation,
  pipeline,
  onDownload,
}) => {
  const canvasRef = useRef(null);
  const [isPanning, setIsPanning] = useState(false);
  const panStartRef = useRef({ x: 0, y: 0 });
  const [isAIPromptOpen, setIsAIPromptOpen] = useState(false);
  const [aiPromptText, setAiPromptText] = useState('');
  const [aiResponse, setAiResponse] = useState(null);

  const handleMouseDown = (e) => {
    if (isCropMode || isResizing || activeOperation === 'resize') return;
    setIsPanning(true);
    panStartRef.current = {
      x: e.clientX - pan.x * zoom,
      y: e.clientY - pan.y * zoom,
    };
  };

  const handleMouseMove = (e) => {
    if (!isPanning) return;
    onPanChange({
      x: (e.clientX - panStartRef.current.x) / zoom,
      y: (e.clientY - panStartRef.current.y) / zoom,
    });
  };

  const handleMouseUp = () => {
    setIsPanning(false);
  };

  const getFilterStyle = () => {
    if (!effectOperation) return {};
    let filters = [];
    if (effectOperation.brightness !== undefined && effectOperation.brightness !== 0) {
      filters.push(`brightness(${1 + effectOperation.brightness})`);
    }
    if (effectOperation.contrast !== undefined && effectOperation.contrast !== 0) {
      filters.push(`contrast(${1 + effectOperation.contrast})`);
    }
    if (effectOperation.saturation !== undefined) {
      filters.push(`saturate(${effectOperation.saturation})`);
    }
    if (effectOperation.enabled) {
      // Mood presets
      const mood = effectOperation.mood;
      if (mood && mood !== 'none') {
        switch (mood) {
          case 'cinematic':
            filters.push('contrast(1.2) saturate(0.85) hue-rotate(-5deg) sepia(0.1)');
            break;
          case 'vintage':
            filters.push('sepia(0.35) contrast(0.92) brightness(1.08)');
            break;
          case 'cyberpunk':
            filters.push('hue-rotate(190deg) saturate(1.8) contrast(1.12)');
            break;
          case 'golden':
            filters.push('sepia(0.25) saturate(1.35) brightness(1.04)');
            break;
          case 'dramatic':
            filters.push('contrast(1.35) brightness(0.96) saturate(1.15)');
            break;
          case 'noir':
            filters.push('grayscale(1) contrast(1.25) brightness(1.08)');
            break;
          default:
            break;
        }
      }

      // Special effects
      const effect = effectOperation.effect;
      if (effect === 'blur') filters.push('blur(3px)');
      if (effect === 'grayscale') filters.push('grayscale(100%)');
      if (effect === 'sepia') filters.push('sepia(100%)');
      if (effect === 'sharpen') filters.push('contrast(1.15) brightness(1.05)');
    }
    if (filters.length === 0) return {};
    return { filter: filters.join(' ') };
  };

  const resizeCrop = cropOperation?.enabled
    ? cropState
    : {
      x: 0,
      y: 0,
      width: resizeOperation?.width || imageSize.width,
      height: resizeOperation?.height || imageSize.height,
    };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    if (!previewDisplayUrl) {
      console.log('StudioCanvas: No previewDisplayUrl provided');
      return;
    }
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.onload = () => {
      const isCropped = cropOperation && cropOperation.enabled;
      if (isCropped) {
        const { x, y, width, height, targetWidth, targetHeight, fillColor } = cropOperation;
        const finalWidth = targetWidth > 0 ? targetWidth : width;
        const finalHeight = targetHeight > 0 ? targetHeight : height;
        canvas.width = finalWidth;
        canvas.height = finalHeight;
        if (finalWidth > width || finalHeight > height) {
          ctx.fillStyle = fillColor || '#000000';
          ctx.fillRect(0, 0, finalWidth, finalHeight);
        }
        const destX = (finalWidth - width) / 2;
        const destY = (finalHeight - height) / 2;
        ctx.drawImage(img, x, y, width, height, destX, destY, width, height);
      } else {
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);
      }
      if (effectOperation && effectOperation.enabled) {
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        if (effectOperation.brightness) applyBrightness(imageData, effectOperation.brightness);
        if (effectOperation.contrast) applyContrast(imageData, effectOperation.contrast);
        if (effectOperation.effect === 'grayscale') applyGrayscale(imageData);
        if (effectOperation.effect === 'sepia') applySepia(imageData);
        ctx.putImageData(imageData, 0, 0);
        if (effectOperation.effect === 'blur') applyBlur(ctx, canvas, 5);
      }
    };
    img.onerror = (err) => {
      console.error('StudioCanvas: Image load error', err, 'for URL:', previewDisplayUrl);
    };
    console.log('StudioCanvas: Loading image from:', previewDisplayUrl.startsWith('blob:') ? 'blob URL' : previewDisplayUrl);
    img.src = previewDisplayUrl;
  }, [previewDisplayUrl, cropOperation, isCropMode, effectOperation, activeOperation, resizeOperation]);

  return (
    <section className="studio-canvas-shell">
      <div
        className="canvas-stage"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        style={{ cursor: isPanning ? 'grabbing' : (previewDisplayUrl && !isCropMode && activeOperation !== 'resize' ? 'grab' : 'default') }}
      >
        {previewDisplayUrl ? (
          <>
            {isCropMode ? (
              <InteractiveCropOverlay
                imageSrc={previewDisplayUrl}
                crop={cropState}
                onCropChange={onCropChange}
                onCropComplete={onCropComplete}
                aspectRatio={cropAspectRatio}
                onAspectRatioChange={onCropAspectRatioChange}
                onClose={onExitCropMode}
                imageSize={imageSize}
                filterStyle={getFilterStyle()}
                watermarkPreview={watermarkPreview}
              />
            ) : resizeOperation?.enabled ? (
              <InteractiveCropOverlay
                imageSrc={previewDisplayUrl}
                crop={resizeCrop}
                onCropChange={(nextCrop) => {
                  const liveCrop = { ...nextCrop, enabled: true };
                  onCropChange(liveCrop);
                  onCropComplete(liveCrop);
                  onResizeChange({ width: nextCrop.width, height: nextCrop.height });
                }}
                onCropComplete={(nextCrop) => {
                  onCropComplete(nextCrop);
                  onResizeComplete({ width: nextCrop.width, height: nextCrop.height });
                }}
                aspectRatio={resizeAspectRatio}
                onAspectRatioChange={onResizeAspectRatioChange}
                onClose={onExitCropMode}
                imageSize={imageSize}
                isResizeMode={true}
                filterStyle={getFilterStyle()}
                watermarkPreview={watermarkPreview}
              />
            ) : (
              <div className="canvas-frame" style={canvasFrameStyle}>
                <div
                  className="canvas-image-transform-layer"
                  style={{
                    transform: `scale(${zoom}) translate(${pan.x}px, ${pan.y}px)`,
                    transition: isPanning ? 'none' : 'transform 0.1s ease-out',
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <canvas ref={canvasRef} className="canvas-image" />
                  {watermarkPreview}
                </div>
                <>
                  <div className="canvas-chip top">Resize Preview</div>
                  <div className="canvas-chip corner">{originalAspectLabel}</div>
                </>
                {/* Floating Zoom Control */}
                <div
                  className="canvas-floating-zoom"
                  onMouseDown={(e) => e.stopPropagation()}
                  onMouseMove={(e) => e.stopPropagation()}
                  onMouseUp={(e) => e.stopPropagation()}
                >
                  <span className="canvas-floating-zoom-label">{Math.round(zoom * 100)}%</span>
                  <input
                    type="range"
                    className="canvas-floating-zoom-slider"
                    min="0.25"
                    max="3"
                    step="0.05"
                    value={zoom}
                    onChange={(e) => onZoomChange(Number(e.target.value))}
                    title={`Zoom: ${Math.round(zoom * 100)}%`}
                  />
                  <button
                    type="button"
                    className="canvas-floating-zoom-reset"
                    onClick={() => { onZoomChange(1); onPanChange({ x: 0, y: 0 }); }}
                    title="Reset Zoom"
                  >
                    <span className="material-symbols-outlined">fit_screen</span>
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <button type="button" className={`drop-zone ${isBatchMode ? 'batch-drop-zone' : ''}`} onClick={onOpenFilePicker}>
            <span className="drop-zone-plus">+</span>
            <div className="drop-zone-title">{isBatchMode ? 'Upload Images' : 'Upload Image'}</div>
          </button>
        )}
      </div>

      {/* Bottom Bar */}
        <div className="canvas-editor-bottom-bar">
          <AnimatePresence>
            {isAIPromptOpen && (
              <motion.div
                initial={{ opacity: 0, y: 15, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 10, scale: 0.95 }}
                transition={{ duration: 0.2, ease: "easeOut" }}
                className="fixed bottom-20 left-0 right-0 m-auto w-[26rem] bg-[#1A1A1A]/95 backdrop-blur-xl border border-white/10 rounded-2xl p-4 shadow-[0_40px_80px_rgba(0,0,0,0.6)] flex flex-col gap-3 z-[200] ring-1 ring-emerald-500/30"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center shrink-0 shadow-inner">
                    <span className="material-symbols-outlined text-emerald-400 text-[16px]">smart_toy</span>
                  </div>
                  <input
                    type="text"
                    autoFocus
                    placeholder="Ask AI (e.g. resize to 3:4, cyberpunk)"
                    className="flex-1 bg-transparent border-none outline-none text-white text-[15px] placeholder:text-white/30 font-medium tracking-wide"
                    value={aiPromptText}
                    onChange={(e) => setAiPromptText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        const response = executeAICommand(aiPromptText, onUpdateOperation, imageSize, resizeOperation);
                        setAiResponse(response);
                        setAiPromptText('');
                        if (response && !response.actions) {
                          setTimeout(() => setAiResponse(null), 3000);
                        }
                      }
                    }}
                  />
                  <button type="button" onClick={() => { setIsAIPromptOpen(false); setAiResponse(null); }} className="text-white/40 hover:text-white flex items-center justify-center w-7 h-7 rounded-full hover:bg-white/10 transition-colors shrink-0">
                    <span className="material-symbols-outlined text-[16px]">close</span>
                  </button>
                </div>
                <AnimatePresence mode="wait">
                  {aiResponse && (
                    <motion.div
                      key={aiResponse.message}
                      initial={{ opacity: 0, height: 0, marginTop: 0 }}
                      animate={{ opacity: 1, height: 'auto', marginTop: 4 }}
                      exit={{ opacity: 0, height: 0, marginTop: 0 }}
                      className="flex flex-col gap-2 px-1.5"
                    >
                      <div className="text-xs text-emerald-400 font-medium">
                        {aiResponse.message}
                      </div>
                      {aiResponse.actions && aiResponse.actions.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-1">
                          {aiResponse.actions.map((action, idx) => (
                            <button
                              key={idx}
                              type="button"
                              onClick={() => {
                                const response = executeAICommand(action.payload, onUpdateOperation, imageSize, resizeOperation);
                                setAiResponse(response);
                                if (response && !response.actions) {
                                  setTimeout(() => setAiResponse(null), 3000);
                                }
                              }}
                              className="text-[10px] uppercase tracking-wider font-bold bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-300 px-3 py-1.5 rounded-full border border-emerald-500/30 transition-colors shadow-sm"
                            >
                              {action.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            )}
          </AnimatePresence>

          <button type="button" className="bottom-bar-action-btn" onClick={onOpenFilePicker} title="Upload New Asset">
            <span className="material-symbols-outlined">upload</span>
          </button>
          <div className="bottom-bar-navigation">
            <button type="button" className={`bottom-bar-nav-btn ${isCropMode || activeOperation === 'resize' ? 'active' : ''}`} onClick={() => onFocusNode('resize')} title="Crop & Canvas">
              <span className="material-symbols-outlined">crop</span>
            </button>
            <button type="button" className={`bottom-bar-nav-btn text-emerald-400 ${isAIPromptOpen ? 'active' : ''}`} onClick={() => setIsAIPromptOpen(!isAIPromptOpen)} title="AI Edit Command">
              <span className="material-symbols-outlined">smart_toy</span>
            </button>
            <button type="button" className={`bottom-bar-nav-btn ${activeOperation === 'effect' ? 'active' : ''}`} onClick={() => onFocusNode('effect')} title="Effects Presets">
              <span className="material-symbols-outlined">auto_awesome</span>
            </button>
            <button type="button" className={`bottom-bar-nav-btn ${activeOperation === 'watermark' ? 'active' : ''}`} onClick={() => onFocusNode('watermark')} title="Watermark Layout">
              <span className="material-symbols-outlined">branding_watermark</span>
            </button>
          </div>
          <button type="button" className="bottom-bar-action-btn" onClick={onDownload} title="Download Result">
            <span className="material-symbols-outlined">download</span>
          </button>
        </div>


    </section>
  );
};

export default StudioCanvas;
